/**
 * WorkspaceProvider — loads the project → folder → style hierarchy from Supabase
 * and writes results into the Zustand workspace store.
 *
 * Degrades silently when /api/workspace endpoints are not yet live (Phase 5).
 * Mutations (createProject, createFolder, createStyle) are no-ops until the
 * corresponding server routes exist.
 */

import { useCallback, type ReactNode } from "react";
import type { Project, Folder, Style } from "../../../shared/db/types.js";
import { useWorkspaceStore } from "../stores/workspace_store.js";

export interface WorkspaceMutations {
  createProject: (name: string) => Promise<Project | null>;
  createFolder:  (projectId: string, name: string) => Promise<Folder | null>;
  createStyle:   (folderId: string, name: string, techpackId?: string) => Promise<Style | null>;
  refreshWorkspace: () => Promise<void>;
}

export function useWorkspace() {
  const { projects, folders, styles, loading, addProject, addFolder, addStyle, setProjects, setFolders, setStyles, setLoading } =
    useWorkspaceStore();

  const token = () => localStorage.getItem("studio_token") ?? "";

  const refreshWorkspace = useCallback(async () => {
    const t = token();
    if (!t) return;
    setLoading(true);
    try {
      const r = await fetch("/api/workspace", { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) return;
      const data = await r.json() as { projects: Project[]; folders: Folder[]; styles: Style[] };
      setProjects(data.projects ?? []);
      setFolders(data.folders  ?? []);
      setStyles(data.styles    ?? []);
    } catch {
      // /api/workspace not yet live — silent until Phase 5
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createProject = useCallback(async (name: string): Promise<Project | null> => {
    const t = token();
    if (!t) return null;
    try {
      const r = await fetch("/api/workspace/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ name }),
      });
      if (!r.ok) return null;
      const proj = await r.json() as Project;
      addProject(proj);
      return proj;
    } catch { return null; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createFolder = useCallback(async (projectId: string, name: string): Promise<Folder | null> => {
    const t = token();
    if (!t) return null;
    try {
      const r = await fetch("/api/workspace/folders", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ project_id: projectId, name }),
      });
      if (!r.ok) return null;
      const folder = await r.json() as Folder;
      addFolder(folder);
      return folder;
    } catch { return null; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createStyle = useCallback(async (folderId: string, name: string, techpackId?: string): Promise<Style | null> => {
    const t = token();
    if (!t) return null;
    try {
      const r = await fetch("/api/workspace/styles", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ folder_id: folderId, name, techpack_id: techpackId }),
      });
      if (!r.ok) return null;
      const style = await r.json() as Style;
      addStyle(style);
      return style;
    } catch { return null; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { projects, folders, styles, loading, createProject, createFolder, createStyle, refreshWorkspace };
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  // Store is module-level (Zustand); no React context needed.
  return <>{children}</>;
}
